// Client portal help content — tutorials (one per page) + FAQs.
// Kept in its own module so the Help page stays about layout, not copy.

export const TUTORIALS = [
  {
    id: 'dashboard',
    icon: '📊',
    title: 'Your dashboard',
    summary: 'Everything that needs your attention, in one screen.',
    steps: [
      'Dashboard is your home screen — open it from the sidebar.',
      'The coloured cards at the top tell you what needs action: quotes waiting for your decision, invoices due, proofs of delivery ready, or your next expected delivery.',
      'The four boxes underneath show active jobs, quotes awaiting you, your outstanding balance and proofs of delivery. Click any box to open that page.',
      'Your quotes, jobs, invoices and PODs each have their own panel — click any row to open it.',
      'Bottom right shows who you are signed in as, plus your company contact details.'
    ]
  },
  {
    id: 'request',
    icon: '📄',
    title: 'Requesting a quote',
    summary: 'Ask us to price a load in under a minute.',
    steps: [
      'Click Request a quote (or New Job in the sidebar).',
      'The panel at the top shows your name, your email and your cell number — every request is logged in YOUR name, not against the company in general.',
      'Choose the quote type: Abnormal, Minerals, Agri, General — or use the Container Estimator for containers.',
      'Enter the commodity, tonnage and the pick-up and delivery addresses.',
      'Add anything we should know in the comments, and attach photos or site files if you have them.',
      'Submit. Our team prices it and you are notified the moment your quote is ready.'
    ],
    tip: 'Your cell number is editable on the request form and saves straight to your profile — we use it for delivery updates.'
  },
  {
    id: 'quotes',
    icon: '💰',
    title: 'Reviewing and accepting quotes',
    summary: 'Accept, reject or download your quotations.',
    steps: [
      'Open My Quotes. Each quote shows its number, the route, the amount and its status.',
      'Click the quote number (or the eye icon) to open the full quote: every leg of the route with its distance, what we are moving, the complete pricing breakdown and the terms.',
      'While a quote shows Sent for Review you can Accept Quote or Reject it — an optional reason helps us sharpen the pricing next time.',
      'Download PDF gives you the official quotation. It is available on sent and accepted quotes; drafts cannot be downloaded until we send them.',
      'Once you accept, the quote moves to Accepted and our team confirms your job.'
    ],
    tip: 'The primary company contact stays on the quote as the accounts contact — the quote itself is issued in your name with your contact details.'
  },
  {
    id: 'jobs',
    icon: '🚚',
    title: 'Tracking your jobs and loads',
    summary: 'Progress, routes and the next expected delivery.',
    steps: [
      'My Jobs lists everything we are running for you, with progress bars and status.',
      'Loads breaks a job into individual truck loads (legs) — open a load to see its timeline of pick-up, border and offloading events.',
      'Live Map shows where your loads are right now.',
      'Completed lists finished jobs with their delivery dates — useful for your own records.'
    ]
  },
  {
    id: 'pods',
    icon: '📝',
    title: 'Proof of delivery (POD)',
    summary: 'Signed delivery notes you can download.',
    steps: [
      'Every delivered load gets a POD: the signed delivery note, who received it and the delivered weight.',
      'Open Proof of Delivery in the sidebar, or click the POD panel on your dashboard.',
      'Each card shows the load, the route, the delivery date, the receiver, weight and commodity.',
      'Click View / download the POD to open the signed document.',
      'While a POD shows Awaiting approval our office is still checking it — it stays visible to you either way.'
    ]
  },
  {
    id: 'invoices',
    icon: '🧾',
    title: 'Invoices and proformas',
    summary: 'What you owe, what is paid, and the documents.',
    steps: [
      'Invoices & Proformas lists every document we have issued to you.',
      'A Proforma is a preview raised before work starts; an Invoice is payable on the terms printed on it.',
      'The status shows where it stands: Awaiting payment, Part paid, Paid, Overdue or Credited.',
      'Open a document for the amounts, VAT, due date and the PDF download.',
      'If a proof of payment has been recorded against an invoice, it is shown on the document detail.'
    ],
    tip: 'Payments and credit notes are captured by our accounts team, so the balance on your dashboard always matches our ledger.'
  },
  {
    id: 'reports',
    icon: '📈',
    title: 'Reports',
    summary: 'Spend and delivery performance.',
    steps: [
      'Reports summarises your spend, load counts and delivery performance for the period you choose.',
      'Use it to reconcile your own records or to share transport volumes with your finance team.'
    ]
  },
  {
    id: 'profile',
    icon: '👤',
    title: 'Your profile and notifications',
    summary: 'Keep your details current so updates reach you.',
    steps: [
      'My Profile holds your name, cell number, email address and password.',
      'Keep your cell number current — it is used for delivery updates.',
      'Company details (trading name, VAT and registration numbers, banking details) are the account-level details your finance team needs.',
      'Settings controls your notifications — leave Email Notifications on so new quotes, job updates and PODs reach you.',
      'Each person at your company has their own login and their own contact details.'
    ]
  }

];

export const FAQS = [
  { q: 'How do I know when a quote is ready?', a: 'You get a notification in the bell (top right) and, if Email Notifications are switched on, an email. The quote also appears at the top of My Quotes with the status Sent for Review.' },
  { q: 'Can I reject a quote?', a: 'Yes. Open the quote and use Reject (an optional reason helps us), or use the reject button on the quotes list.' },
  { q: 'Why can I not download a draft quote?', a: 'Draft quotes are still being prepared internally. The PDF becomes available the moment we send the quote to you, and stays available after you accept it.' },
  { q: 'Who is the contact person shown on my quotes?', a: 'That is your company primary/accounts contact on our system. The request itself is logged in your name, with your email and cell number.' },
  { q: 'Where do I see proof of delivery?', a: 'Proof of Delivery in the sidebar. A POD appears for every completed load, with the receiver, weight and the signed document to download.' },
  { q: 'How do I update my cell number?', a: 'My Profile → your details. You can also change it while requesting a quote — it saves straight to your profile.' },
  { q: 'Something looks wrong on an invoice', a: 'Contact your account manager using the details at the bottom of this page, or reply to the invoice email. Our accounts team will correct it and issue a credit note if needed.' },
  { q: 'How do I get more people from my company onto the portal?', a: 'Your company administrator can invite colleagues under My Team, or ask us and we will send the invitation.' },
  { q: 'Which browsers work best?', a: 'The portal works on any current version of Chrome, Edge, Safari or Firefox — desktop, tablet or phone.' }
];
